import { InputAdvisability } from 'views/components/molecules/establishes/inputAdvisability';
import { InputConsent } from 'views/components/molecules/establishes/inputConsent';
import { InputChoices } from 'views/components/molecules/establishes/inputChoices';
import { InputLetter } from 'views/components/molecules/establishes/inputLetter';
import {
  CostomerInfoFormikType,
  NationalityStrs,
  PrefectureStrs,
  SEX_TYPE,
  getSexType,
  FormErrorScrollTarget,
  Nationality,
} from 'views/utils/accountEstablish';
import { Button, ButtonProps } from 'views/components/atoms/button';
import { EstablishError } from 'views/components/atoms/establishes/error';
import { EstablishHeader } from 'views/components/atoms/establishes/header';
import { EstablishText, MaskAttr } from 'views/components/atoms/establishes/text';
import { EstablishCheck } from 'views/components/atoms/establishes/check';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ja from 'date-fns/locale/ja';
import { toKatakana, toZenkaku } from 'views/utils/format/FormatString';
registerLocale('ja', ja);

type CustomerInfoTemplateType = {
  formik: CostomerInfoFormikType;
  buttonProps?: {
    nextButtonProps: ButtonProps;
    prevButtonProps: ButtonProps;
    confirmButtonProps: ButtonProps;
    isPrevPageConfirm: boolean;
  };
  searchButtonProps: ButtonProps;
  getAddressInfo: () => void;
  isUpdate: boolean;
  maskMap?: MaskMap;
};

type MaskMap = {
  mobilePhoneNumber: MaskAttr;
  homePhoneNumber: MaskAttr;
  mailAddress: MaskAttr;
};

export const CustomerInfoTemplate = ({
  formik,
  buttonProps,
  searchButtonProps,
  getAddressInfo,
  isUpdate,
  maskMap: maskTypes,
}: CustomerInfoTemplateType) => {
  return (
    <>
      <div className="establishWrapper">
        {!!formik.errors.nationality && <FormErrorScrollTarget />}
        <InputChoices
          title="国籍"
          batchDangerWord="必須"
          selectedOption={formik.values.nationality ? formik.values.nationality : ''}
          setSelectedOption={(selectedOption) => {
            formik.setFieldValue('nationality', selectedOption);
            if (selectedOption === Nationality.USA) {
              formik.setFieldValue('americanOnTaxSystem', true);
            }
          }}
          options={NationalityStrs}
          isEnabled={!!formik.errors.nationality}
          errorText={formik.errors.nationality}
        />
      </div>
      <div className="establishWrapper hiddenBg">
        <div className="establishWrapper_announce">
          <h1>FATCAに関するご質問</h1>
          <span>
            お客さまとのお取引にあたり、弊社およびFPG信託は米国税法(Foreign Account Tax Compliance
            Act)、当該法制に関する日米政府間合意および日本当局からの各金融機関に対する要請に基づき、犯罪収益移転防止法による取引時確認とは別に所定の本人確認の実施、記録の作成・保存等が求められています
          </span>
        </div>

        {!!formik.errors.americanOnTaxSystem && <FormErrorScrollTarget />}
        <InputAdvisability
          title="米国税法上の米国人（米国永住権保持者等）に該当しますか？"
          name="invitation"
          batchDangerWord="必須"
          agreeText="はい"
          denialText="いいえ"
          agreeValue="true"
          denialValue="false"
          checked={formik.values.americanOnTaxSystem ?? undefined}
          onChange={(checked) => formik.setFieldValue('americanOnTaxSystem', checked)}
          isEnabled={!!formik.errors.americanOnTaxSystem}
          errorText={formik.errors.americanOnTaxSystem}
          reverse={true}
          disabled={formik.values.nationality === Nationality.USA}
        />
      </div>
      {formik.values.americanOnTaxSystem && (
        <>
          <div className="establishWrapper hiddenBg">
            <div className="establishWrapper_announce">
              <h1>SSN（米国社会保障番号）について</h1>
              <span>
                SSN（米国社会保障番号）をお持ちの方は、SSNを入力してください。SSNをお持ちでない方は、「ITIN（米国納税者番号）」を入力してください。
              </span>
            </div>
            {!!formik.errors.ssn && <FormErrorScrollTarget />}
            <InputLetter
              inputType="tel"
              title="SSN（米国社会保障番号）"
              inputValue={formik.values.ssn ?? undefined}
              setInputValue={(e) => formik.setFieldValue('ssn', e.target.value)}
              isEnable={!!formik.errors.ssn}
              batchDangerWord="必須"
              errorText={formik.errors.ssn}
              placeholder="SSNを入力してください"
            />
          </div>
          <div className="establishWrapper hiddenBg">
            {!!formik.errors.itin && <FormErrorScrollTarget />}
            <InputLetter
              inputType="tel"
              title="ITIN（米国納税者番号※SSNをお持ちでない場合）"
              inputValue={formik.values.itin ?? undefined}
              setInputValue={(e) => formik.setFieldValue('itin', e.target.value)}
              batchDangerWord="必須"
              isEnable={!!formik.errors.itin}
              errorText={formik.errors.itin}
              placeholder="ITINを入力してください"
            />
          </div>
          <div className="establishWrapper hiddenBg">
            <div className="establishWrapper_announce">
              <h1>【FATCAについて】</h1>
              <span>
                下記文言をご確認いただき、申告内容をご了承いただいた上で、チェックを付けてください。
                なお、この情報・同意・宣誓は、お客さまが購入される受益証券の受託者であるFPG信託にもご提出いただいたものとして取り扱わさせていただきます。
              </span>
            </div>
          </div>
          <div className="establishWrapper hiddenBg">
            {!!formik.errors.fatcaAgreement && <FormErrorScrollTarget />}
            <InputConsent
              title="FATCAに関する同意"
              name="fatcaAgreement"
              batchDangerWord="必須"
              checkText="私は、FATCA上の要請に基づき貴社およびFPG信託が、私に関する以下の情報を、米国当局または/および国内当局に開示することに同意します"
              isChecked={formik.values.fatcaAgreement ? formik.values.fatcaAgreement : false}
              setIsChecked={(e) => formik.setFieldValue('fatcaAgreement', e.target.checked)}
              isEnable={!!formik.errors.fatcaAgreement}
              errorText={formik.errors.fatcaAgreement}
            />
            <span className="establishWrapper_description">
              ※米国当局・国内当局に対し提供するお客さまの情報 :
              氏名・住所・米国社会保険番号ないし米国納税者番号・取引内容や残高等の口座情報等
            </span>
            {!!formik.errors.fatcaOath && <FormErrorScrollTarget />}
            <InputConsent
              title="FATCAに関する宣誓"
              name="fatcaOath"
              batchDangerWord="必須"
              checkText="上記内容について正確であることを宣誓します。 上記記載内容に変更があった場合には、３０日以内に連絡いたします。"
              isChecked={formik.values.fatcaOath ? formik.values.fatcaOath : false}
              setIsChecked={(e) => formik.setFieldValue('fatcaOath', e.target.checked)}
              isEnable={!!formik.errors.fatcaOath}
              errorText={formik.errors.fatcaOath}
            />
            <span className="establishWrapper_description">
              ※米国当局・国内当局に対し提供するお客さまの情報 :
              氏名・住所・米国社会保険番号ないし米国納税者番号・取引内容や残高等の口座情報等
            </span>
          </div>
        </>
      )}
      <div className="establishWrapper">
        <div className="inputWrapper">
          {(!!formik.errors.lastName || !!formik.errors.firstName) && <FormErrorScrollTarget />}
          <EstablishHeader headerTitle={'氏名'} batchDangerWord="必須" />

          <div className="flexWrapper">
            <EstablishText
              inputType="text"
              inputValue={formik.values.lastName ?? undefined}
              setInputValue={(e) => formik.setFieldValue('lastName', e.target.value)}
              placeholder="姓（全角）"
              onBlur={() => {
                // 全角にできるものは変換
                const name = toZenkaku(formik.values.lastName);
                formik.setFieldValue('lastName', name);
              }}
            />
            <EstablishText
              inputType="text"
              inputValue={formik.values.firstName ?? undefined}
              setInputValue={(e) => formik.setFieldValue('firstName', e.target.value)}
              placeholder="名（全角）"
              onBlur={() => {
                // 全角にできるものは変換
                const name = toZenkaku(formik.values.firstName);
                formik.setFieldValue('firstName', name);
              }}
            />
          </div>
          <div className="flexWrapper">
            <EstablishError
              isEnabled={!!formik.errors.lastName || !!formik.errors.firstName}
              errorText={formik.errors.lastName || formik.errors.firstName}
            />
            {/* <EstablishError isEnabled={!!formik.errors.firstName} errorText={formik.errors.firstName} /> */}
          </div>

          {(!!formik.errors.lastNameKana || !!formik.errors.firstNameKana) && <FormErrorScrollTarget />}
          <EstablishHeader headerTitle={'氏名（フリガナ）'} batchDangerWord="必須" />
          <div className="flexWrapper">
            <EstablishText
              inputType="text"
              inputValue={formik.values.lastNameKana ?? undefined}
              setInputValue={(e) => formik.setFieldValue('lastNameKana', e.target.value)}
              placeholder="姓（全角カタカナ）"
              onBlur={() => {
                // 全角+平仮名はカナに変換
                const name = toKatakana(toZenkaku(formik.values.lastNameKana));
                formik.setFieldValue('lastNameKana', name);
              }}
            />
            <EstablishText
              inputType="text"
              inputValue={formik.values.firstNameKana ?? undefined}
              setInputValue={(e) => formik.setFieldValue('firstNameKana', e.target.value)}
              placeholder="名（全角カタカナ）"
              onBlur={() => {
                // 全角+平仮名はカナに変換
                const name = toKatakana(toZenkaku(formik.values.firstNameKana));
                formik.setFieldValue('firstNameKana', name);
              }}
            />
          </div>
          <div className="flexWrapper">
            <EstablishError
              isEnabled={!!formik.errors.lastNameKana || !!formik.errors.firstNameKana}
              errorText={formik.errors.lastNameKana || formik.errors.firstNameKana}
            />
            {/* <EstablishError isEnabled={!!formik.errors.firstNameKana} errorText={formik.errors.firstNameKana} /> */}
          </div>
        </div>
      </div>
      {!isUpdate && (
        <div className="establishWrapper">
          <div className="inputWrapper">
            {!!formik.errors.birthday && <FormErrorScrollTarget />}
            <EstablishHeader headerTitle={'生年月日'} batchDangerWord="必須" />
            <DatePicker
              className="datePicker"
              onChange={(d: Date) => {
                formik.setFieldValue('birthday', !!d ? d.toLocaleDateString() : null);
              }}
              selected={!!formik.values.birthday ? new Date(formik.values.birthday) : null}
              dateFormat="yyyy/MM/dd"
              locale="ja"
              dateFormatCalendar="yyyy年 MM月"
              showPopperArrow={false}
              openToDate={new Date('1980/01/01')}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              placeholderText="YYYY/MM/DD"
            />

            <EstablishError isEnabled={!!formik.errors.birthday} errorText={formik.errors.birthday} />
          </div>
        </div>
      )}
      {!isUpdate && (
        <div className="establishWrapper">
          {!!formik.errors.sex && <FormErrorScrollTarget />}
          <InputAdvisability
            title="性別"
            name="sex"
            batchDangerWord="必須"
            agreeText="男性"
            denialText="女性"
            agreeValue="true"
            denialValue="false"
            checked={getSexType(formik.values.sex)}
            onChange={(checked) => formik.setFieldValue('sex', checked ? SEX_TYPE.MAN : SEX_TYPE.WOMAN)}
            isEnabled={!!formik.errors.sex}
            errorText={formik.errors.sex}
          />
        </div>
      )}
      <div className="establishWrapper">
        <div className="inputWrapper">
          {!!formik.errors.postCode && <FormErrorScrollTarget />}
          <EstablishHeader headerTitle={'住所'} batchDangerWord="必須" />
          <EstablishHeader headerTitle={'郵便番号'} />
          <div className="flexWrapper">
            <EstablishText
              inputType="tel"
              inputValue={formik.values.postCode ?? undefined}
              setInputValue={(e) => formik.setFieldValue('postCode', e.target.value)}
              onKeyEnter={getAddressInfo}
              placeholder="例:1234567（ハイフン不要）"
            />
            <Button {...searchButtonProps} />
          </div>
          <span className="establishWrapper_description establishWrapper_description_search">
            ※ 正しく表示されない場合は、ご修正ください
          </span>
          <EstablishError isEnabled={!!formik.errors.postCode} errorText={formik.errors.postCode} />
        </div>
        <div className="inputWrapper">
          {!!formik.errors.prefecture && <FormErrorScrollTarget />}
          <InputChoices
            title="都道府県"
            selectedOption={formik.values.prefecture ? formik.values.prefecture : ''}
            setSelectedOption={(selectedOption) => formik.setFieldValue('prefecture', selectedOption)}
            options={PrefectureStrs}
            isEnabled={!!formik.errors.prefecture}
            errorText={formik.errors.prefecture}
          />
        </div>
        <div className="inputWrapper">
          {!!formik.errors.cityCounty && <FormErrorScrollTarget />}
          <EstablishHeader headerTitle={'市区郡'} />
          <EstablishText
            inputType="text"
            inputValue={formik.values.cityCounty ?? undefined}
            setInputValue={(e) => formik.setFieldValue('cityCounty', e.target.value)}
            placeholder="市区郡（例　千代田区）"
          />
          <EstablishError isEnabled={!!formik.errors.cityCounty} errorText={formik.errors.cityCounty} />
        </div>
        <div className="inputWrapper">
          {!!formik.errors.townStreetAddress && <FormErrorScrollTarget />}
          <EstablishHeader headerTitle={'町名'} />
          <div className="flexWrapper">
            <EstablishText
              inputType="text"
              inputValue={formik.values.townStreetAddress ?? undefined}
              setInputValue={(e) => formik.setFieldValue('townStreetAddress', e.target.value)}
              placeholder="町名（例　丸の内２丁目）"
            />
          </div>
          <EstablishError isEnabled={!!formik.errors.townStreetAddress} errorText={formik.errors.townStreetAddress} />

          {!!formik.errors.blockNumber && <FormErrorScrollTarget />}
          <EstablishHeader headerTitle={'補足住所（郵便番号検索で自動的に表示されない住所をご入力ください）'} />
          <div className="flexWrapper">
            <EstablishText
              inputType="text"
              inputValue={formik.values.blockNumber ?? undefined}
              setInputValue={(e) => formik.setFieldValue('blockNumber', e.target.value)}
              placeholder="補足住所（例　７番２号）"
              disabled={formik.values.isNoBlockNumber}
            />
            <EstablishCheck
              name="isNoBlockNumber"
              text="補足住所なし"
              isChecked={formik.values.isNoBlockNumber}
              setIsChecked={() => {
                if (!!formik.values.blockNumber) {
                  formik.setFieldError('blockNumber', '補足住所を入力している場合は、補足住所なしを選択できません。');
                  return;
                }
                formik.setFieldValue('blockNumber', '');
                formik.setFieldError('blockNumber', undefined); // チェックしたらエラー解消
                formik.setFieldValue('isNoBlockNumber', !formik.values.isNoBlockNumber);
              }}
            />
          </div>
          <EstablishError isEnabled={!!formik.errors.blockNumber} errorText={formik.errors.blockNumber} />

          <div className="inputWrapper">
            {!!formik.errors.buildingNameRoomNumber && <FormErrorScrollTarget />}
            <EstablishHeader headerTitle={'建物名・部屋番号'} />
            <div className="flexWrapper">
              <EstablishText
                inputType="text"
                inputValue={formik.values.buildingNameRoomNumber ?? undefined}
                setInputValue={(e) => formik.setFieldValue('buildingNameRoomNumber', e.target.value)}
                placeholder="建物名・部屋番号（例　JPタワー　２９階）"
                disabled={formik.values.isNoBuildingNameRoomNumber}
              />
              <EstablishCheck
                name="isNoBuildingNameRoomNumber"
                text="建物名・部屋番号なし"
                isChecked={formik.values.isNoBuildingNameRoomNumber}
                setIsChecked={() => {
                  if (!!formik.values.buildingNameRoomNumber) {
                    formik.setFieldError(
                      'buildingNameRoomNumber',
                      '補建物名・部屋番号を入力している場合は、建物名・部屋番号なしを選択できません。',
                    );
                    return;
                  }

                  formik.setFieldValue('buildingNameRoomNumber', '');
                  formik.setFieldError('buildingNameRoomNumber', undefined);
                  formik.setFieldValue('isNoBuildingNameRoomNumber', !formik.values.isNoBuildingNameRoomNumber);
                }}
              />
            </div>
            <EstablishError
              isEnabled={!!formik.errors.buildingNameRoomNumber}
              errorText={formik.errors.buildingNameRoomNumber}
            />
          </div>
        </div>
      </div>
      <div className="establishWrapper">
        <div className="inputWrapper">
          {!!formik.errors.mobilePhoneNumber && <FormErrorScrollTarget />}
          <EstablishHeader headerTitle={'携帯電話番号'} batchDangerWord="必須" />
          <EstablishText
            inputType="tel"
            inputValue={formik.values.mobilePhoneNumber ?? undefined}
            setInputValue={(e) => formik.setFieldValue('mobilePhoneNumber', e.target.value)}
            placeholder="例:09012345678（ハイフン不要）"
            masked={maskTypes?.mobilePhoneNumber}
          />
          <EstablishError isEnabled={!!formik.errors.mobilePhoneNumber} errorText={formik.errors.mobilePhoneNumber} />
        </div>
      </div>
      <div className="establishWrapper">
        <div className="inputWrapper">
          {!!formik.errors.homePhoneNumber && <FormErrorScrollTarget />}
          <EstablishHeader headerTitle={'自宅電話番号'} batchAnyWord="任意" />
          <EstablishText
            inputType="tel"
            inputValue={formik.values.homePhoneNumber ?? undefined}
            setInputValue={(e) => formik.setFieldValue('homePhoneNumber', e.target.value)}
            placeholder="例:0312345678（ハイフン不要）"
            masked={maskTypes?.homePhoneNumber}
          />
          <EstablishError isEnabled={!!formik.errors.homePhoneNumber} errorText={formik.errors.homePhoneNumber} />{' '}
        </div>
      </div>
      <div className="establishWrapper">
        <div className="inputWrapper">
          {!!formik.errors.mailAddress && <FormErrorScrollTarget />}
          <EstablishHeader headerTitle={'メールアドレス'} batchDangerWord="必須" />
          <EstablishText
            inputType="text"
            inputValue={formik.values.mailAddress ?? undefined}
            setInputValue={(e) => formik.setFieldValue('mailAddress', e.target.value)}
            placeholder="例:example@example.com"
            masked={maskTypes?.mailAddress}
          />
          <EstablishError isEnabled={!!formik.errors.mailAddress} errorText={formik.errors.mailAddress} />
        </div>
      </div>
      {buttonProps && (
        <div className="establishWrapper">
          {buttonProps.isPrevPageConfirm ? (
            <div className="establishWrapper_buttons">
              <Button {...buttonProps.confirmButtonProps} />
            </div>
          ) : (
            <div className="establishWrapper_buttons">
              <Button {...buttonProps.prevButtonProps} />
              <Button {...buttonProps.nextButtonProps} />
            </div>
          )}
        </div>
      )}
    </>
  );
};
